import { Clock, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CheckCircleIcon from '@/assets/icons/check-circle.svg';
import ReloadIcon from '@/assets/icons/reload.svg';
import XCircleIcon from '@/assets/icons/x-circle.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';

import { directApplyQuestionnaireMessages } from '../job-details/posting-requirements/direct-apply-questionnaire';
import type { OpeningChannelResult } from '../types';
import type { useRetryPostingItem } from '../use-posting-batch';

interface ChannelStatusRowProps {
  channel: OpeningChannelResult;
  /** A posting batch's Retry; rows without it never show Retry. */
  retry?: ReturnType<typeof useRetryPostingItem>;
}

function StatusLabel({ status }: { status: OpeningChannelResult['status'] }) {
  switch (status) {
    case 'queued':
      return (
        <>
          <Clock className="text-fx-neutral-400 size-4" aria-hidden />
          {/* neutral-500, not the design's neutral-400: 400 fails AA contrast on neutral-50. */}
          <Typography variant="small1" className="text-fx-neutral-500 italic">
            <FormattedMessage
              defaultMessage="Queued"
              description="Status label when a channel posting waits to start"
            />
          </Typography>
        </>
      );
    case 'posting':
      return (
        <>
          <Loader2
            className="text-fx-primary size-4 animate-spin motion-reduce:animate-none"
            aria-hidden
          />
          <Typography variant="small1" className="text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Posting"
              description="Status label when a channel posting is in progress"
            />
          </Typography>
        </>
      );
    case 'posted':
      return (
        <>
          <img src={CheckCircleIcon} alt="" />
          <Typography variant="small1" className="text-fx-green-800">
            <FormattedMessage
              defaultMessage="Posted"
              description="Status label when a channel posting succeeded"
            />
          </Typography>
        </>
      );
    default:
      return (
        <>
          <img src={XCircleIcon} alt="" />
          <Typography variant="small1" className="text-fx-rose">
            <FormattedMessage
              defaultMessage="Failed"
              description="Status label when a channel posting failed"
            />
          </Typography>
        </>
      );
  }
}

function useFailureReason(channel: OpeningChannelResult): ReactNode {
  const intl = useIntl();
  if (channel.reason === 'questions_changed') {
    return intl.formatMessage(
      directApplyQuestionnaireMessages.questionsChanged,
    );
  }
  switch (channel.reasonCode) {
    case 'no_answer':
      return intl.formatMessage({
        defaultMessage: "The job board didn't respond.",
        description:
          'Reason under a failed posting row when the job board never answered',
      });
    case 'board_error':
      return intl.formatMessage({
        defaultMessage: 'The job board returned an error.',
        description:
          'Reason under a failed posting row when the job board answered with an error',
      });
    case 'unconfirmed':
      return intl.formatMessage({
        defaultMessage:
          "We couldn't confirm this posting with the job board. Retry checks with the job board first, so it won't be posted or charged twice.",
        description:
          'Reason under a failed paid posting row whose order the job board never confirmed',
      });
    case 'overlap':
      return intl.formatMessage(
        {
          defaultMessage:
            '{count, plural, one {1 opening} other {{count} openings}} have campaigns that overlap.',
          description: 'Error message for overlapping sourcing purchases',
        },
        { count: 1 },
      );
    case 'not_permitted':
      return intl.formatMessage({
        defaultMessage: 'You no longer have permission to post jobs.',
        description:
          'Reason under a failed posting row when the user lost the permission to post jobs',
      });
    default:
      // job_details carries the board's own message.
      return channel.reasonMessage ?? null;
  }
}

export function ChannelStatusRow({ channel, retry }: ChannelStatusRowProps) {
  const intl = useIntl();
  // Rows of a background posting batch carry an item; today's result step's do not.
  const isBatchRow = channel.itemId !== undefined;
  const reason = useFailureReason(channel);
  const itemId = channel.itemId;
  const canRetry =
    channel.status === 'failed' &&
    channel.retryable === true &&
    itemId !== undefined &&
    retry !== undefined;

  let note: ReactNode = null;
  if (channel.status === 'failed' && (reason || canRetry)) {
    note = (
      // rose-800, not the label's rose: rose reads 4.49:1 on the card's neutral-50.
      <Typography variant="small1" className="text-fx-rose-800">
        {reason}{' '}
        {canRetry && (
          <Button
            type="button"
            variant="link"
            className="text-small h-auto gap-1 align-middle"
            disabled={retry.isRetrying(itemId)}
            aria-label={intl.formatMessage(
              {
                defaultMessage: 'Retry {channelName}',
                description:
                  'Accessible name of the Retry link on a failed posting row',
              },
              { channelName: channel.displayTitle },
            )}
            onClick={() => retry.retry(itemId)}
          >
            <img src={ReloadIcon} alt="" className="size-3" />
            <FormattedMessage
              defaultMessage="Retry"
              description="Link that retries one failed job board posting"
            />
          </Button>
        )}
      </Typography>
    );
  } else if (channel.paid && channel.status === 'posting') {
    if (channel.note === 'waiting_confirmation') {
      note = (
        <Typography variant="small1" className="text-fx-neutral-500">
          <FormattedMessage
            defaultMessage="Waiting for the job board to confirm."
            description="Note under a paid posting row whose order awaits the job board's confirmation"
          />
        </Typography>
      );
    }
  } else if (channel.paid && channel.status === 'posted') {
    note = (
      <Typography variant="small1" className="text-fx-neutral-500">
        <FormattedMessage
          defaultMessage="Goes live once the board confirms, usually within {hours} hours."
          description="Note under a paid posted row: the board publishes it after its own review"
          values={{
            hours:
              channel.processingTimeHours ??
              intl.formatMessage({
                defaultMessage: 'a few',
                description:
                  'Default processing time for a campaign when not set',
              }),
          }}
        />
      </Typography>
    );
  }

  return (
    <li className="flex flex-col gap-1 px-3">
      <div className="flex items-start justify-between gap-3">
        <ChannelIcon
          channelSlug={channel.channelSlug}
          variant={isBatchRow ? 'small' : 'large'}
          size="xs"
          logoSquareUrl={channel.logoSquareUrl}
          logoRectangleUrl={channel.logoRectangleUrl}
          channelName={channel.channelName}
          displayTitle={channel.displayTitle}
          titleClassName="text-fx-neutral-600 font-normal"
        />

        <div className="flex shrink-0 items-center gap-1">
          <StatusLabel status={channel.status} />
        </div>
      </div>
      {note}
    </li>
  );
}
